import { useQuery, useQueryClient } from "@tanstack/react-query";

import { customAxios } from "lib/axios";
import { Organization } from "types";
import { refetchUserOnError } from "utils/refetchUserOnError";

export type PaginationData = {
  page: number;
  limit: number;
  total: number;
  pages: number;
  hasMore: boolean;
};

export type PaginatedOrganization = Organization & {
  pagination?: PaginationData;
};

const getOrg = async (
  orgId: string | undefined,
  page = 1,
  limit = 10,
  email?: string
): Promise<PaginatedOrganization> => {
  if (!orgId) {
    return Promise.reject(new Error("Invalid orgId"));
  }

  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (email) {
    params.append("email", email);
  }

  const { data } = await customAxios.get<PaginatedOrganization>(
    `/organizations/${orgId}?${params.toString()}`
  );

  return data;
};

export const useGetOrg = (
  orgId: string | undefined,
  page = 1,
  limit = 10,
  email?: string
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["org", orgId, page, limit, email], // ✅ email in cache key
    queryFn: () => getOrg(orgId, page, limit, email),
    enabled: !!orgId,
    onError: (err) => {
      refetchUserOnError(err, queryClient);
    },
  });
};
